import { type ClientId } from '../../client/bridge-api-client-types';

import { findTopmostSameOriginWindow, isTop } from './browser-apis';

const MAX_FRAME_TRAVERSAL_DEPTH = 16;

const toFrameId = (receiverId: ClientId): string =>
	`[data-rovo-receiver-id="${receiverId}"]`;

const getDocument = (targetWindow: Window): Document | undefined => {
	try {
		return targetWindow.document;
	} catch {
		return undefined;
	}
};

const getFrames = (targetWindow: Window): Window[] => {
	try {
		return Array.from(targetWindow.frames);
	} catch {
		return [];
	}
};

/**
 * Traverses through all child frames (same-origin) which the current window has access to.
 * Required to discover child frames which are deeply nested as transport targets.
 * 
 * @param receiverId bridge API client we are attempting to communicate with
 * @param rootWindow the current host / root window we are traversing down
 * @param visited windows we have already searched through
 * @param depth current depth - to enforce a maximum depth in our DFS
 * 
 * @returns the relevant window or undefined
 */
const resolveChildIframeTargetFromWindow = (
	receiverId: ClientId,
	rootWindow: Window,
	visited: WeakSet<Window>,
	depth: number,
): Window | undefined => {
	// if we have already searched this window, early exit
	if (visited.has(rootWindow)) {
		return undefined;
	}

	// if we can no longer traverse further downwards, early exit
	if (depth > MAX_FRAME_TRAVERSAL_DEPTH) {
		return undefined;
	}

	// mark as visited
	visited.add(rootWindow);

	// get hold of the document & look for the current receiver of interest
	const rootDocument = getDocument(rootWindow);
	const frame = rootDocument?.querySelector<HTMLIFrameElement>(toFrameId(receiverId));
	if (frame?.contentWindow) {
		return frame.contentWindow;
	}

	// if not found at the top-level, traverse down
	for (const childWindow of getFrames(rootWindow)) {
		const target = resolveChildIframeTargetFromWindow(
			receiverId,
			childWindow,
			visited,
			depth + 1,
		);

		// found in sub-frame
		if (target) {
			return target;
		}
	}

	// not found amongst this window & all of its sub-frames
	return undefined;
};

/**
 * Resolves a target window by querying same-origin reachable DOM trees for an iframe with a
 * specific data attribute.
 *
 * @description The bridge API is mounted on the topmost same-origin window, but the target iframe
 * may be nested inside another same-origin iframe. Starting from the topmost same-origin window and
 * walking accessible child frame documents lets the shared transport discover cross-origin frames
 * that are only present inside nested same-origin containers.
 *
 * @param receiverId - The receiver ID to search for
 * @returns The iframe's contentWindow if found, otherwise undefined
 */
export const resolveChildIframeTarget = (receiverId: ClientId): Window | undefined => {
	if (typeof document === 'undefined') {
		return undefined;
	}

	return resolveChildIframeTargetFromWindow(
		receiverId,
		findTopmostSameOriginWindow(window),
		new WeakSet<Window>(),
		0,
	);
};

/**
 * Resolves a target window by checking if communication should go to window.top.
 * This is used when the current window is inside an iframe and needs to communicate with the parent.
 *
 * @returns window.top if available and we're not at the top level, otherwise undefined
 */
export const resolveParentTarget = (): Window | undefined => {
	if (window.top && !isTop(window)) {
		return window.top;
	}

	return undefined;
};

/**
 * Finds a target window for cross-origin communication.
 * Tries multiple strategies in order:
 * 1. Look for a child iframe with the matching receiver ID
 * 2. Use window.top if we're inside an iframe
 *
 * @param receiverId - The receiver ID to find a target for
 * @returns The target window if found, otherwise undefined
 */
export const findCrossOriginTarget = (receiverId: ClientId): Window | undefined => {
	// Try to find a child iframe first (works at any level)
	const browserTarget = resolveChildIframeTarget(receiverId);
	if (browserTarget) {
		return browserTarget;
	}

	// If not a child, assume parent communication via window.top
	return resolveParentTarget();
};
